import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconClockPause,
  IconFlame,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { useLocation } from "wouter";
import { jsonGet, jsonPut } from "../api";
import MonitoringConfigPanel from "./MonitoringConfigPanel";
import {
  actionColor,
  actionLabel,
  kindLabel,
  needsAction,
  sentimentColor,
  sentimentLabel,
} from "./mentionDisplay";

const PAGE_SIZE = 25;

const ACTION_OPTIONS = [
  { value: "", label: "All mentions" },
  { value: "respondPublicly", label: "Respond publicly" },
  { value: "correctFactualError", label: "Correct an error" },
  { value: "createContent", label: "Create content" },
  { value: "reviewManually", label: "Review manually" },
];

const KIND_OPTIONS = [
  { value: "", label: "Any type" },
  { value: "review", label: "Review" },
  { value: "news", label: "News" },
  { value: "forum", label: "Forum" },
  { value: "comparison", label: "Comparison" },
  { value: "listicle", label: "Listicle" },
  { value: "blog", label: "Blog" },
];

const STATUS_OPTIONS = [
  { value: "new", label: "Open" },
  { value: "actioned", label: "Actioned" },
  { value: "dismissed", label: "Dismissed" },
  { value: "", label: "All" },
];

function StatTile({ label, children }) {
  return (
    <Paper withBorder p="md">
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      {children}
    </Paper>
  );
}

export default function WebMentions({ brand }) {
  const [, setLocation] = useLocation();
  const [mentions, setMentions] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [allowance, setAllowance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [domainFilter, setDomainFilter] = useState("");
  const [action, setAction] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("new");

  const load = useCallback(async () => {
    if (!brand?.id) return;
    setLoading(true);

    // Filtering server-side, not in the browser: the page is capped and the old version filtered
    // the fetched page only, so "no mentions match" could mean "none on page 1".
    const params = new URLSearchParams({
      skip: String((page - 1) * PAGE_SIZE),
      limit: String(PAGE_SIZE),
    });
    if (domainFilter.trim()) params.set("domain", domainFilter.trim());
    if (action) params.set("action", action);
    if (kind) params.set("kind", kind);
    if (status) params.set("status", status);

    try {
      const [list, s, a] = await Promise.all([
        jsonGet(`brands/${brand.id}/web-mentions?${params}`),
        jsonGet(`brands/${brand.id}/web-mentions/stats`),
        jsonGet(`brands/${brand.id}/firehose/allowance`).catch(() => null),
      ]);
      setMentions(list?.items ?? []);
      setTotal(list?.total ?? 0);
      setStats(s);
      setAllowance(a);
    } catch {
      setMentions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [brand?.id, page, domainFilter, action, kind, status]);

  useEffect(() => {
    load();
  }, [load]);

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(1);
  }, [domainFilter, action, kind, status]);

  async function setMentionStatus(mention, next) {
    await jsonPut(`brands/${brand.id}/web-mentions/${mention.id}/status`, { status: next });
    load();
  }

  if (loading && mentions.length === 0 && !stats) return <Loader m="xl" />;

  const hasAnyMentions = (stats?.count ?? 0) > 0 || mentions.length > 0;

  if (!hasAnyMentions) {
    return (
      <Stack mt="md" align="center" py="xl">
        <Paper withBorder p="xl" radius="md" maw={480} w="100%">
          <Stack align="center" gap="md">
            <ThemeIcon size={48} radius="xl" variant="light" color="orange">
              <IconFlame size={24} />
            </ThemeIcon>
            <Title order={4} ta="center">
              No web mentions yet
            </Title>
            <Text c="dimmed" ta="center" maw={360}>
              We watch the web for pages that mention your brand, then classify each one — what it
              says, how it reads, and whether it is worth responding to.
            </Text>
            <Button variant="light" onClick={() => setLocation("/settings?tab=setup")}>
              Enable in brand settings
            </Button>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Stack mt="md">
      {/* Monitoring is paced across the month rather than spent first-come, so a quiet page needs an
          explanation — and the two pauses mean different things to the reader. */}
      {allowance && !allowance.active && (
        <Alert
          color={allowance.pausedReason === "monthlyBudgetExhausted" ? "orange" : "blue"}
          icon={<IconClockPause size={16} />}
          title={
            allowance.pausedReason === "monthlyBudgetExhausted"
              ? "This month's monitoring budget is used up"
              : "Today's monitoring allowance is used up"
          }
        >
          {allowance.pausedReason === "monthlyBudgetExhausted" ? (
            <>
              We've collected {(allowance.mentionsThisPeriod ?? 0).toLocaleString()} mentions
              this month. Monitoring resumes at the start of next month — upgrade your plan to raise the
              budget.
            </>
          ) : (
            <>
              Your budget is spread across the month so it lasts, rather than being spent in the first
              few days. Monitoring resumes at 00:00 UTC.{" "}
              {allowance.mentionsRemainingThisPeriod > 0 && (
                <>
                  {allowance.mentionsRemainingThisPeriod.toLocaleString()} mentions left this month.
                </>
              )}
            </>
          )}
        </Alert>
      )}

      {/* Above the numbers: what is being watched, and the controls to change it. Someone reading
          a thin week needs to be able to ask "is this quiet, or am I watching the wrong things?" */}
      <MonitoringConfigPanel brand={brand} allowance={allowance} />

      {stats && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          <StatTile label="Total mentions">
            <Title order={3}>{stats.count}</Title>
          </StatTile>

          <StatTile label="Needs a response">
            <Group gap="xs" align="baseline">
              <Title order={3}>{stats.needsActionCount ?? 0}</Title>
              {(stats.needsActionCount ?? 0) > 0 && (
                <Badge color="orange" variant="light">
                  open
                </Badge>
              )}
            </Group>
          </StatTile>

          <StatTile label="Sentiment">
            <Group gap="xs" align="baseline">
              <Title order={3}>
                {stats.avgSentiment != null ? stats.avgSentiment.toFixed(2) : "—"}
              </Title>
              {stats.avgSentiment != null && (
                <Badge color={sentimentColor(stats.avgSentiment)} variant="light">
                  {sentimentLabel(stats.avgSentiment)}
                </Badge>
              )}
            </Group>
            {(stats.positiveCount > 0 || stats.negativeCount > 0) && (
              <Text size="xs" c="dimmed" mt={4}>
                {stats.positiveCount} positive · {stats.negativeCount} negative
              </Text>
            )}
          </StatTile>

          <StatTile label="What they talk about">
            {stats.topAspects?.length ? (
              <Stack gap={2} mt={4}>
                {stats.topAspects.slice(0, 4).map((a) => (
                  <Group key={a.aspect} gap={6} wrap="nowrap">
                    <Badge
                      size="xs"
                      variant="light"
                      color={sentimentColor(a.avgSentiment)}
                    >
                      {a.count}
                    </Badge>
                    <Text size="sm" lineClamp={1}>
                      {a.aspect}
                    </Text>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed" mt={4}>
                Nothing specific yet
              </Text>
            )}
          </StatTile>
        </SimpleGrid>
      )}

      <Group gap="sm" wrap="wrap">
        <TextInput
          placeholder="Filter by domain…"
          leftSection={<IconSearch size={14} />}
          value={domainFilter}
          onChange={(e) => setDomainFilter(e.currentTarget.value)}
          w={220}
        />
        <Select data={ACTION_OPTIONS} value={action} onChange={(v) => setAction(v ?? "")} w={190} />
        <Select data={KIND_OPTIONS} value={kind} onChange={(v) => setKind(v ?? "")} w={160} />
        <Select data={STATUS_OPTIONS} value={status} onChange={(v) => setStatus(v ?? "")} w={140} />
      </Group>

      {mentions.length === 0 ? (
        <Text c="dimmed" ta="center" mt="xl">
          No mentions match these filters.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Mention</Table.Th>
              <Table.Th w={110}>Type</Table.Th>
              <Table.Th w={100}>Date</Table.Th>
              <Table.Th w={110}>Sentiment</Table.Th>
              <Table.Th w={170}>Suggested action</Table.Th>
              <Table.Th w={80} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {mentions.map((m) => (
              <Table.Tr key={m.id}>
                <Table.Td maw={460}>
                  <Stack gap={2}>
                    <Anchor href={m.url} target="_blank" rel="noopener noreferrer" size="sm" lineClamp={1}>
                      {m.title || m.url}
                    </Anchor>
                    <Text size="xs" c="dimmed">
                      {m.domain}
                    </Text>
                    {m.keyQuote && (
                      <Text size="xs" fs="italic" c="dimmed" lineClamp={2}>
                        “{m.keyQuote}”
                      </Text>
                    )}
                    {m.aspects?.length > 0 && (
                      <Group gap={4} mt={2}>
                        {m.aspects.map((a) => (
                          <Badge key={a} size="xs" variant="outline" color="gray">
                            {a}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{kindLabel(m.kind)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {m.publishTime
                      ? new Date(m.publishTime).toLocaleDateString()
                      : m.discoveredAt
                        ? new Date(m.discoveredAt).toLocaleDateString()
                        : "—"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={sentimentColor(m.sentimentScore)} variant="light" size="sm">
                    {m.analyzed && m.sentimentScore != null
                      ? m.sentimentScore.toFixed(2)
                      : "Pending"}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {needsAction(m) ? (
                    <Tooltip label={m.actionReason} disabled={!m.actionReason} multiline w={260}>
                      <Badge color={actionColor(m.suggestedAction)} variant="light" size="sm">
                        {actionLabel(m.suggestedAction)}
                      </Badge>
                    </Tooltip>
                  ) : (
                    <Text size="xs" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {m.status === "new" ? (
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="Mark as handled">
                        <ActionIcon
                          variant="subtle"
                          color="teal"
                          onClick={() => setMentionStatus(m, "actioned")}
                        >
                          <IconCheck size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Dismiss">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => setMentionStatus(m, "dismissed")}
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  ) : (
                    <Text size="xs" c="dimmed">
                      {m.status === "actioned" ? "Handled" : "Dismissed"}
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {pageCount > 1 && (
        <Group justify="center" mt="sm">
          <Pagination total={pageCount} value={page} onChange={setPage} size="sm" />
        </Group>
      )}
    </Stack>
  );
}
